import { ReactNode } from 'react';
import { BasicInvestment, Rating } from '../../apis/backend';

export type FilterKey = 'stocks' | 'funds' | 'large' | 'quality' | 'lowFees';

export const filterOptions: Record<
  FilterKey,
  {
    buttonComponent: ReactNode;
    filterFn: (investment: Partial<BasicInvestment>) => boolean;
  }
> = {
  stocks: {
    buttonComponent: 'Stocks',
    filterFn: (investment) => !!investment.category && ['STOCK', 'ADR'].includes(investment.category),
  },
  funds: {
    buttonComponent: 'Funds',
    filterFn: (investment) => !!investment.category && ['MUTUAL_FUND', 'ETF'].includes(investment.category),
  },
  large: {
    buttonComponent: 'Large cap',
    filterFn: (investment) => (investment?.marketCap ?? 0) > 5 * 10 ** 9,
  },
  quality: {
    buttonComponent: 'Quality',
    filterFn: (investment) => {
      if (!investment?.bottomLine) return false;

      const isETFOrFund = investment.category && ['MUTUAL_FUND', 'ETF'].includes(investment.category);

      let ratings;
      if (isETFOrFund) {
        // For ETFs/Funds: Check return, risk, size, fees ratings
        ratings = [
          investment.bottomLine.returnRating,
          investment.bottomLine.riskRating,
          investment.bottomLine.sizeRating,
          investment.bottomLine.feesRating,
        ];
      } else {
        // For Stocks: Check profit, growth, price ratings
        ratings = [
          investment.bottomLine.profitRating,
          investment.bottomLine.growthRating,
          investment.bottomLine.priceRating,
        ];
      }

      const favorableCount = ratings.filter(
        (rating) => rating && [Rating.Favorable, Rating.Exceptional].includes(rating),
      ).length;

      // Require at least 2 favorable ratings for both stocks and ETFs
      return favorableCount >= 2;
    },
  },
  lowFees: {
    buttonComponent: 'Low fees',
    filterFn: (investment) => {
      // Only apply to ETFs and Mutual Funds
      if (!investment.category || !['MUTUAL_FUND', 'ETF'].includes(investment.category)) {
        return false;
      }

      // Check if feesRating is Favorable or Exceptional
      return (
        !!investment?.bottomLine?.feesRating &&
        [Rating.Favorable, Rating.Exceptional].includes(investment.bottomLine.feesRating)
      );
    },
  },
};
